/**
 * <ns-carousel> — horizontal scroll-snap track with prev/next controls.
 * Progressive enhancement: without JS the track still scrolls natively.
 */
if (!customElements.get('ns-carousel')) {
  customElements.define(
    'ns-carousel',
    class NsCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.ns-carousel__track');
        this.prev = this.querySelector('.ns-carousel__btn--prev');
        this.next = this.querySelector('.ns-carousel__btn--next');
        if (!this.track) return;

        this.onScroll = this.throttle(() => this.syncButtons());
        this.track.addEventListener('scroll', this.onScroll, { passive: true });

        if (this.prev) this.prev.addEventListener('click', () => this.scrollByPage(-1));
        if (this.next) this.next.addEventListener('click', () => this.scrollByPage(1));

        // Track width drives how many cells a page is, so re-sync on resize.
        this.resizeObserver = new ResizeObserver(() => this.syncButtons());
        this.resizeObserver.observe(this.track);

        this.syncButtons();
      }

      disconnectedCallback() {
        if (this.track) this.track.removeEventListener('scroll', this.onScroll);
        if (this.resizeObserver) this.resizeObserver.disconnect();
      }

      get step() {
        const cell = this.track.firstElementChild;
        if (!cell) return this.track.clientWidth;
        // Advance by whole cells so cards never land half-cropped.
        const cellWidth = cell.getBoundingClientRect().width;
        const perPage = Math.max(1, Math.floor(this.track.clientWidth / cellWidth));
        return cellWidth * perPage;
      }

      scrollByPage(direction) {
        const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
        this.track.scrollBy({ left: this.step * direction, behavior });
      }

      syncButtons() {
        const max = this.track.scrollWidth - this.track.clientWidth;
        const at = this.track.scrollLeft;
        // 1px tolerance absorbs sub-pixel rounding at the track ends.
        if (this.prev) this.prev.disabled = at <= 1;
        if (this.next) this.next.disabled = at >= max - 1;
      }

      throttle(fn) {
        let frame = null;
        return () => {
          if (frame) return;
          frame = requestAnimationFrame(() => {
            frame = null;
            fn();
          });
        };
      }
    }
  );
}
